import React, { useState, useEffect } from 'react';
import './DynamicAddressTable.css';

interface AddressEntry {
  poBox?: string;
  aptNumber: string;
  streetNumber: string;
  streetName: string;
  city: string;
  country: string;
  province: string;
  postalCode: string;
  district: string;
}

interface AddressData {
  mailingAddress: AddressEntry;
  residentialSameAsMailing: boolean;
  residentialAddress?: AddressEntry;
  residentialAddressYes?: string;
  residentialAddressNo?: string;
}

interface DynamicAddressTableProps {
  value: AddressData;
  onChange: (value: AddressData) => void;
  required?: boolean;
}

const DynamicAddressTable: React.FC<DynamicAddressTableProps> = ({
  value,
  onChange,
  required = false
}) => {
  const [addressData, setAddressData] = useState<AddressData>(value || {
    mailingAddress: {
      aptNumber: '',
      streetNumber: '',
      streetName: '',
      city: '',
      country: '',
      province: '',
      postalCode: '',
      district: ''
    },
    residentialSameAsMailing: true,
    residentialAddressYes: 'Y',
    residentialAddressNo: '',
    residentialAddress: undefined
  });

  useEffect(() => {
    setAddressData(value || {
      mailingAddress: {
        aptNumber: '',
        streetNumber: '',
        streetName: '',
        city: '',
        country: '',
        province: '',
        postalCode: '',
        district: ''
      },
      residentialSameAsMailing: true,
      residentialAddressYes: 'Y',
      residentialAddressNo: '',
      residentialAddress: undefined
    });
  }, [value]);

  const updateMailingAddress = (field: keyof AddressEntry, value: string) => {
    const updatedData = {
      ...addressData,
      mailingAddress: {
        ...addressData.mailingAddress,
        [field]: value
      }
    };
    
    // If residential same as mailing, update residential address too
    if (addressData.residentialSameAsMailing) {
      updatedData.residentialAddress = {
        ...updatedData.mailingAddress
      };
    }
    
    setAddressData(updatedData);
    onChange(updatedData);
  };

  const updateResidentialAddress = (field: keyof AddressEntry, value: string) => {
    const updatedData = {
      ...addressData,
      residentialAddress: {
        ...addressData.residentialAddress!,
        [field]: value
      }
    };
    setAddressData(updatedData);
    onChange(updatedData);
  };

  const handleResidentialSameAsMailing = (same: boolean) => {
    let updatedData: AddressData;
    
    if (same) {
      // Copy mailing address to residential
      updatedData = {
        ...addressData,
        residentialSameAsMailing: true,
        residentialAddressYes: 'Y',
        residentialAddressNo: '',
        residentialAddress: {
          ...addressData.mailingAddress
        }
      };
    } else {
      // Create new residential address or keep existing
      updatedData = {
        ...addressData,
        residentialSameAsMailing: false,
        residentialAddressYes: '',
        residentialAddressNo: 'N',
        residentialAddress: addressData.residentialAddress || {
          aptNumber: '',
          streetNumber: '',
          streetName: '',
          city: '',
          country: '',
          province: '',
          postalCode: '',
          district: ''
        }
      };
    }
    
    setAddressData(updatedData);
    onChange(updatedData);
  };

  const renderAddressForm = (address: AddressEntry, isMailing: boolean = true, title: string) => (
    <div className="entry-card">
      <div className="entry-header">
        <div className="entry-number">
          <span className="entry-badge">{isMailing ? '🏠' : '📍'}</span>
          <h5>{title}</h5>
        </div>
      </div>
      
      <div className="entry-form">
        <div className="form-row form-row-1">
          {isMailing && (
            <div className="field-group">
              <label>P.O. Box</label>
              <input
                type="text"
                value={address.poBox || ''}
                onChange={(e) => isMailing ? updateMailingAddress('poBox', e.target.value) : updateResidentialAddress('poBox', e.target.value)}
                placeholder="1234"
              />
            </div>
          )}
          
          <div className="field-group">
            <label>Apt/Unit</label>
            <input
              type="text"
              value={address.aptNumber}
              onChange={(e) => isMailing ? updateMailingAddress('aptNumber', e.target.value) : updateResidentialAddress('aptNumber', e.target.value)}
              placeholder="Apt 5B"
            />
          </div>
          
          <div className="field-group">
            <label>Street #</label>
            <input
              type="text"
              value={address.streetNumber}
              onChange={(e) => isMailing ? updateMailingAddress('streetNumber', e.target.value) : updateResidentialAddress('streetNumber', e.target.value)}
              placeholder="123"
            />
          </div>
          
          <div className="field-group">
            <label>Street Name</label>
            <input
              type="text"
              value={address.streetName}
              onChange={(e) => isMailing ? updateMailingAddress('streetName', e.target.value) : updateResidentialAddress('streetName', e.target.value)}
              placeholder="Main Street"
            />
          </div>
        </div>
        
        <div className="form-row form-row-2">
          <div className="field-group">
            <label>City</label>
            <input
              type="text"
              value={address.city}
              onChange={(e) => isMailing ? updateMailingAddress('city', e.target.value) : updateResidentialAddress('city', e.target.value)}
              placeholder="Toronto"
            />
          </div>
          
          <div className="field-group">
            <label>Country</label>
            <input
              type="text"
              value={address.country}
              onChange={(e) => isMailing ? updateMailingAddress('country', e.target.value) : updateResidentialAddress('country', e.target.value)}
              placeholder="Canada"
            />
          </div>
          
          <div className="field-group">
            <label>Province/State</label>
            <input
              type="text"
              value={address.province}
              onChange={(e) => isMailing ? updateMailingAddress('province', e.target.value) : updateResidentialAddress('province', e.target.value)}
              placeholder="Ontario"
            />
          </div>
          
          <div className="field-group">
            <label>Postal Code</label>
            <input
              type="text"
              value={address.postalCode}
              onChange={(e) => isMailing ? updateMailingAddress('postalCode', e.target.value) : updateResidentialAddress('postalCode', e.target.value)}
              placeholder="M5V 2H1"
            />
          </div>
          
          <div className="field-group">
            <label>District</label>
            <input
              type="text"
              value={address.district}
              onChange={(e) => isMailing ? updateMailingAddress('district', e.target.value) : updateResidentialAddress('district', e.target.value)}
              placeholder="Downtown"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dynamic-address-table">
      {/* Mailing Address Section */}
      <div className="address-section">
        <h4>Address Information</h4>
        <div className="address-container">
          {renderAddressForm(addressData.mailingAddress, true, "Current Mailing Address")}
        </div>
      </div>

      {/* Residential Address Section */}
      <div className="address-section">
        <div className="residential-question">
          <h4>Residential Address same as Mailing Address</h4>
          <div className="same-address-options">
            <label className="radio-option">
              <input
                type="radio"
                name="residentialSameAsMailing"
                checked={addressData.residentialSameAsMailing}
                onChange={() => handleResidentialSameAsMailing(true)}
              />
              <span>Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="residentialSameAsMailing"
                checked={!addressData.residentialSameAsMailing}
                onChange={() => handleResidentialSameAsMailing(false)}
              />
              <span>No</span>
            </label>
          </div>
        </div>

        {!addressData.residentialSameAsMailing && (
          <div className="residential-address-container">
            <p className="residential-note">Please provide your residential address details:</p>
            <div className="address-container">
              {renderAddressForm(addressData.residentialAddress!, false, "Residential Address")}
            </div>
          </div>
        )}
      </div>

      {required && (
        <small className="error-text">Address information is required</small>
      )}
    </div>
  );
};

export default DynamicAddressTable; 